/**
 * «Выйти без сохранения?» — the inline confirmation for × on step 1 once text was
 * entered (handoff §3, proposed). The look is the app's existing confirmation
 * pattern: a centred surface card over a scrim, secondary and ghost actions, no
 * red (nothing is destroyed beyond what was typed).
 */
import { useEffect } from 'react'
import { useLocale } from '../../i18n/context'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'
import { GhostButton, SecondaryPill } from './ui'

interface Props {
  open: boolean
  onStay: () => void
  onLeave: () => void
  /** Other words for the same sheet (edit mode's «Отменить изменения?»). */
  copy?: { title: string; body: string; leave: string; stay: string }
  /** The leaving action in the destructive fill: something the learner made is lost. */
  destructive?: boolean
}

export default function LeaveConfirm({ open, onStay, onLeave, copy, destructive = false }: Props) {
  const { tr } = useLocale()
  const c = copy ?? tr.textFlow.leave
  useSwipeNavBlock(open, 'text-flow-leave')

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onStay() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onStay])

  if (!open) return null
  return (
    <div
      className={CONTROL_SURFACE}
      onContextMenu={suppressTouchContextMenu}
      style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div
        onClick={onStay}
        className="add-sheet-scrim"
        style={{ position: 'absolute', inset: 0 }}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="tf-leave-title"
        style={{
          position: 'relative', width: '100%', maxWidth: 360, padding: 20, borderRadius: 16,
          background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-float)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <h2 id="tf-leave-title" style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{c.title}</h2>
        <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.45, color: 'var(--text-secondary)' }}>{c.body}</p>
        <div style={destructive
          ? { display: 'flex', flexDirection: 'column', gap: 8 }
          : { display: 'flex', justifyContent: 'flex-end', gap: 8 }}
        >
          {destructive ? (
            <button
              type="button"
              onClick={onLeave}
              data-testid="leave-confirm"
              className={CONTROL_SURFACE}
              style={{
                height: 44, padding: '0 16px', borderRadius: 'var(--radius-pill)', border: 'none', cursor: 'pointer', width: '100%',
                background: 'var(--danger-fill)', color: 'var(--text-on-dark)', fontFamily: 'var(--font-ui)',
                fontSize: 14, fontWeight: 600,
              }}
            >
              {c.leave}
            </button>
          ) : (
            <GhostButton onClick={onLeave}>{c.leave}</GhostButton>
          )}
          <SecondaryPill onClick={onStay} autoFocus full={destructive}>{c.stay}</SecondaryPill>
        </div>
      </div>
    </div>
  )
}
